import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    PROGRESS_PROCESSES,
    resetProgress,
} from '@/store/slices/progressSlice';
import { useAppSelector } from '@/store/store';
import { NewUserStorageDataIF } from '@/types/common';
import { getLocalStorageItem } from '@/helpers/storage';
import { syncNewUserStorageData } from '@/helpers/utils';
import Button from '@/components/controls/Button/Button.component';
import Loading from '@/components/elems/Loading/Loading.component';
import LoadCompletedModal from '@/components/widgets/ProgressWidget/LoadCompletedModal/LoadCompletedModal.component';
import LoadFallModal from '@/components/widgets/ProgressWidget/LoadFallModal/LoadFallModal.component';
import LoadStartModal from '@/components/widgets/ProgressWidget/LoadStartModal/LoadStartModal.component';
import {
    PROGRESS_LOADING_HEIGHT,
    PROGRESS_LOADING_WIDTH,
} from '@/components/widgets/ProgressWidget/ProgressWidget.config';
import { getIcon } from '@/components/widgets/ProgressWidget/ProgressWidget.helpers';
import styles from '@/components/widgets/ProgressWidget/ProgressWidget.module.scss';
import { ProgressWidgetPropsIF } from '@/components/widgets/ProgressWidget/ProgressWidget.types';
import {
    TASK_STATUSES,
    TASK_STATUSES_LOADING,
    TaskStatusT,
} from '@/constants/statuses';
import { STORAGE_KEYS } from '@/constants/storageKeys';

const ProgressWidget: FC<ProgressWidgetPropsIF> = ({ className = '' }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { progressStatus, progressConfig, progressProcess } = useAppSelector(
        (state) => state.progressSlice,
    );

    const storageData = getLocalStorageItem<NewUserStorageDataIF>(
        STORAGE_KEYS.NewUserFlags,
    );

    const [isShowLoadStartModal, setIsShowLoadStartModal] =
        useState<boolean>(false);

    const statusData = progressStatus ? progressConfig[progressStatus] : null;
    const { label, icon, button } = statusData || {};

    const finishProgress = (link?: string): void => {
        if (progressProcess === PROGRESS_PROCESSES.Upload) {
            syncNewUserStorageData({
                isShowUploadSuccessModal: false,
            });
        }
        dispatch(resetProgress());

        if (link) {
            navigate(link);
        }
    };

    const closeStartProcessModal = (): void => {
        setIsShowLoadStartModal(false);

        syncNewUserStorageData({
            isShowUploadStartModal: false,
        });
    };

    useEffect(() => {
        if (progressProcess !== PROGRESS_PROCESSES.Upload) {
            setIsShowLoadStartModal(false);
            return;
        }

        if (
            progressStatus === TASK_STATUSES.Uploading ||
            progressStatus === TASK_STATUSES.Failed
        ) {
            setIsShowLoadStartModal(!!storageData?.isShowUploadStartModal);
        }
    }, [progressProcess, progressStatus, storageData?.isShowUploadStartModal]);

    if (!progressStatus) {
        return null;
    }

    if (progressStatus === TASK_STATUSES.FailedAll) {
        return <LoadFallModal />;
    }

    return (
        <>
            <div
                className={`flc ${styles.progressWidgetWrapper} ${styles[progressStatus.toLowerCase()]} ${className}`}
            >
                {label && <div className={styles.label}>{label}</div>}
                <div className={styles.extra}>
                    {Object.values<TaskStatusT>(TASK_STATUSES_LOADING).includes(
                        progressStatus,
                    ) &&
                        !icon &&
                        !button && (
                            <Loading
                                type="pulse"
                                width={PROGRESS_LOADING_WIDTH}
                                height={PROGRESS_LOADING_HEIGHT}
                            />
                        )}
                    {getIcon(icon)}
                    {(button || progressStatus === TASK_STATUSES.Failed) && (
                        <Button
                            className={styles.button}
                            clickHandler={() => finishProgress(button?.link)}
                            variant="accentSmall"
                            dataTest="progress_widget_extra_button"
                        >
                            {button?.text ||
                                (progressStatus === TASK_STATUSES.Failed
                                    ? 'Закрыть'
                                    : '')}
                        </Button>
                    )}
                </div>
            </div>
            {isShowLoadStartModal && (
                <LoadStartModal onclose={closeStartProcessModal} />
            )}
            {progressProcess === PROGRESS_PROCESSES.Upload &&
                !!storageData?.isShowUploadSuccessModal &&
                progressStatus === TASK_STATUSES.Completed &&
                !isShowLoadStartModal && (
                    <LoadCompletedModal onclose={finishProgress} />
                )}
        </>
    );
};

export default ProgressWidget;
